/* =========================================================================
 * Kkumi · Wishlist Search — the matching engine (no DOM)
 * -------------------------------------------------------------------------
 * window.KkwlSearch — pure functions the wishlist grid uses to filter and
 * rank saved items in place as the shopper types (no page reload). The grid
 * (assets/kkumi-wishlist-grid.js) owns the DOM; the toolbar
 * (assets/kkumi-wishlist-toolbar.js) owns the input. This file only answers
 * "does this item match, and how well?".
 *
 *   parse(query, keywords) -> parsed query
 *     Pulls natural-language filters out of the text first — "sale",
 *     "in stock", "out of stock", "under 20", "over $10", "<15", ">5",
 *     "10-20" — then splits what is left into search terms. The filter words
 *     are merchant-editable (toolbar settings → Search), passed in as
 *     comma-separated lists: { sale, instock, oos, under, over }.
 *   score(record, parsed) -> number (0 = no match)
 *     Every term must match somewhere (AND). Per term the best of: exact
 *     word > word prefix > substring (incl. across spaces, so "standloader"
 *     finds "Stand Loader") > typo-tolerant fuzzy match (1 edit for 4+ chars,
 *     2 for 8+, transpositions count as one). Weighted by field: title >
 *     variant > vendor / type > tags. Whole-phrase and title-prefix bonuses.
 *   highlight(text, parsed) -> escaped HTML with <mark> around matched runs.
 *   fold(str) -> lower-case, accent-free form used for all comparisons.
 *
 * record = { title, variant, vendor, type, tags, priceCents, available, onSale }
 * ========================================================================= */
(function () {
  'use strict';
  if (window.KkwlSearch) return;

  // Lower-case + strip Latin combining accents, per character, so an index
  // map back to the original string survives (needed for highlighting).
  // NFC after the strip keeps Hangul syllables composed.
  function foldChar(c) {
    var s = c.toLowerCase();
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '').normalize('NFC');
    return s;
  }
  function foldMap(str) {
    str = String(str == null ? '' : str);
    var out = '', map = [];
    for (var i = 0; i < str.length; i++) {
      var c = str.charAt(i);
      // Keep surrogate pairs together.
      var code = str.charCodeAt(i);
      if (code >= 0xd800 && code <= 0xdbff && i + 1 < str.length) { c += str.charAt(i + 1); }
      var f = foldChar(c);
      for (var k = 0; k < f.length; k++) { out += f.charAt(k); map.push(i); }
      i += c.length - 1;
    }
    return { text: out, map: map };
  }
  function fold(str) { return foldMap(str).text; }

  var WORD_SPLIT = /[^0-9a-zÀ-￿]+/;
  function words(str) { return fold(str).split(WORD_SPLIT).filter(Boolean); }
  function compact(str) { return fold(str).replace(/[^0-9a-zÀ-￿]+/g, ''); }

  function list(csv) {
    return String(csv || '').split(',').map(function (s) { return words(s).join(' '); }).filter(Boolean)
      .sort(function (a, b) { return b.length - a.length; }); // longest first: "out of stock" before "stock"
  }
  function reEsc(s) { return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }
  function num(s) { return Math.round(parseFloat(String(s).replace(',', '.')) * 100); }

  // Remove every occurrence of a phrase (whole words) from q; true if found.
  function take(state, phrase) {
    var re = new RegExp('(^|\\s)' + reEsc(phrase).replace(/ /g, '\\s+') + '(?=\\s|$)', 'g');
    if (!re.test(state.q)) return false;
    state.q = state.q.replace(re, ' ');
    return true;
  }

  var CURRENCY = '[$€£¥₩]?';
  var AMOUNT = '(\\d+(?:[.,]\\d{1,2})?)';

  function parse(query, keywords) {
    keywords = keywords || {};
    var raw = String(query || '').trim();
    var state = { q: ' ' + fold(raw).replace(/\s+/g, ' ') + ' ' };
    var filters = { sale: false, instock: false, oos: false, min: null, max: null };

    // Price words need an amount right after them, so "under" alone stays a term.
    function priceWords(csv, key) {
      list(csv).forEach(function (w) {
        var re = new RegExp('(^|\\s)' + reEsc(w).replace(/ /g, '\\s+') + '\\s*' + CURRENCY + '\\s*' + AMOUNT + '(?=\\s|$)', 'g');
        state.q = state.q.replace(re, function (m, pre, amt) { filters[key] = num(amt); return ' '; });
      });
    }
    priceWords(keywords.under, 'max');
    priceWords(keywords.over, 'min');
    state.q = state.q
      .replace(new RegExp('(^|\\s)<=?\\s*' + CURRENCY + '\\s*' + AMOUNT + '(?=\\s|$)', 'g'), function (m, pre, amt) { filters.max = num(amt); return ' '; })
      .replace(new RegExp('(^|\\s)>=?\\s*' + CURRENCY + '\\s*' + AMOUNT + '(?=\\s|$)', 'g'), function (m, pre, amt) { filters.min = num(amt); return ' '; })
      .replace(new RegExp('(^|\\s)' + CURRENCY + AMOUNT + '\\s*[-–]\\s*' + CURRENCY + AMOUNT + '(?=\\s|$)', 'g'), function (m, pre, a, b) {
        var x = num(a), y = num(b);
        filters.min = Math.min(x, y); filters.max = Math.max(x, y); return ' ';
      });

    // Stock / sale words. "out of stock" is checked before "in stock".
    list(keywords.oos).forEach(function (w) { if (take(state, w)) filters.oos = true; });
    list(keywords.instock).forEach(function (w) { if (take(state, w)) filters.instock = true; });
    list(keywords.sale).forEach(function (w) { if (take(state, w)) filters.sale = true; });
    if (filters.oos && filters.instock) { filters.oos = filters.instock = false; } // contradictory: ignore both

    var terms = state.q.split(WORD_SPLIT).filter(Boolean);
    var hasFilter = filters.sale || filters.instock || filters.oos || filters.min != null || filters.max != null;
    return {
      raw: raw,
      terms: terms,
      phrase: terms.join(' '),
      filters: filters,
      fuzzy: keywords.fuzzy !== false,
      empty: !terms.length && !hasFilter
    };
  }

  // Optimal-string-alignment distance with an early exit once > max.
  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    var prev2 = null, prev = [], cur, i, j;
    for (j = 0; j <= b.length; j++) prev[j] = j;
    for (i = 1; i <= a.length; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= b.length; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (prev2 && i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prev2[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev; prev = cur;
    }
    return prev[b.length];
  }
  function allowedEdits(term) { return term.length >= 8 ? 2 : term.length >= 4 ? 1 : 0; }

  // Best score for one term against one field ({ words, compact }).
  function termScore(term, field, fuzzy) {
    var best = 0;
    for (var i = 0; i < field.words.length; i++) {
      var w = field.words[i];
      if (w === term) return 10;
      if (w.indexOf(term) === 0) { best = Math.max(best, 7); continue; }
      if (term.length >= 2 && w.indexOf(term) > 0) best = Math.max(best, 4);
    }
    if (best < 4 && term.length >= 3 && field.compact.indexOf(term) >= 0) best = Math.max(best, 4);
    if (best || !fuzzy) return best;
    var max = allowedEdits(term);
    if (!max) return 0;
    for (var k = 0; k < field.words.length; k++) {
      var word = field.words[k];
      // Whole word, or the word's same-length prefix (typo while still typing).
      if (distance(term, word, max) <= max) return 3;
      if (word.length > term.length && distance(term, word.slice(0, term.length), max) <= max) best = 2;
    }
    return best;
  }

  var WEIGHTS = { title: 3, variant: 2, vendor: 1.5, type: 1.5, tags: 1 };

  function prepare(record) {
    if (record._kkwlFields) return record._kkwlFields;
    var f = {};
    Object.keys(WEIGHTS).forEach(function (k) {
      var v = record[k];
      if (Array.isArray(v)) v = v.join(' ');
      f[k] = { words: words(v), compact: compact(v), text: fold(v) };
    });
    try { Object.defineProperty(record, '_kkwlFields', { value: f }); } catch (e) { record._kkwlFields = f; }
    return f;
  }

  function score(record, parsed) {
    if (!parsed || parsed.empty) return 1;
    var fl = parsed.filters;
    if (fl.sale && !record.onSale) return 0;
    if (fl.instock && record.available === false) return 0;
    if (fl.oos && record.available !== false) return 0;
    if (fl.min != null || fl.max != null) {
      var p = record.priceCents;
      if (p == null || isNaN(p)) return 0;
      if (fl.min != null && p < fl.min) return 0;
      if (fl.max != null && p > fl.max) return 0;
    }
    if (!parsed.terms.length) return 1;

    var fields = prepare(record);
    var total = 0;
    for (var t = 0; t < parsed.terms.length; t++) {
      var best = 0;
      for (var k in WEIGHTS) {
        var s = termScore(parsed.terms[t], fields[k], parsed.fuzzy) * WEIGHTS[k];
        if (s > best) best = s;
      }
      if (!best) return 0; // every term must land somewhere
      total += best;
    }
    if (parsed.terms.length > 1 && fields.title.text.indexOf(parsed.phrase) >= 0) total += 15;
    if (fields.title.text.indexOf(parsed.terms[0]) === 0) total += 5;
    return total;
  }

  // Escaped HTML with <mark> around the runs of `text` each term matched
  // (exact / prefix / substring). Fuzzy-only matches aren't marked.
  function highlight(text, parsed) {
    var esc = window.KKEsc || function (s) { return String(s); };
    text = String(text == null ? '' : text);
    if (!parsed || !parsed.terms || !parsed.terms.length) return esc(text);
    var fm = foldMap(text);
    var hit = [];
    parsed.terms.forEach(function (term) {
      var from = 0, at;
      while ((at = fm.text.indexOf(term, from)) >= 0) {
        for (var i = at; i < at + term.length; i++) hit[fm.map[i]] = true;
        from = at + term.length;
      }
    });
    var html = '', open = false;
    for (var i = 0; i < text.length; i++) {
      if (hit[i] && !open) { html += '<mark class="kkwl-hl">'; open = true; }
      if (!hit[i] && open) { html += '</mark>'; open = false; }
      html += esc(text.charAt(i));
    }
    if (open) html += '</mark>';
    return html;
  }

  window.KkwlSearch = { parse: parse, score: score, highlight: highlight, fold: fold, _distance: distance };
})();
